import React, { createContext, useContext, useState, useEffect } from 'react'
import { loginUser, logoutUser, getCurrentUser, isAuthenticated, refreshSession } from '../services/authService'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check localStorage first for immediate response
        const authToken = localStorage.getItem('sri_velmurugan_auth')
        const userStr = localStorage.getItem('sri_velmurugan_user')
        
        if (authToken === 'true' && userStr) {
          const currentUser = JSON.parse(userStr)
          setUser(currentUser)
          setIsAuthenticatedState(true)
        } else {
          // If no localStorage data, check with service
          const currentUser = getCurrentUser()
          const authStatus = isAuthenticated()
          
          if (authStatus && currentUser) {
            setUser(currentUser)
            setIsAuthenticatedState(true)
          }
        }
      } catch (error) {
        console.error('Auth check error:', error)
        // Clear invalid data
        localStorage.removeItem('sri_velmurugan_auth')
        localStorage.removeItem('sri_velmurugan_user')
        localStorage.removeItem('sri_velmurugan_session_start')
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Session refresh mechanism - refresh session every 30 minutes while user is active
  useEffect(() => {
    if (isAuthenticatedState && user) {
      const refreshInterval = setInterval(() => {
        const refreshed = refreshSession()
        if (!refreshed) {
          // Session expired, logout user
          logout()
        }
      }, 30 * 60 * 1000) // 30 minutes

      // Also refresh on user activity
      const handleUserActivity = () => {
        refreshSession()
      }

      // Add event listeners for user activity
      window.addEventListener('mousemove', handleUserActivity)
      window.addEventListener('keydown', handleUserActivity)
      window.addEventListener('click', handleUserActivity)
      window.addEventListener('scroll', handleUserActivity)

      return () => {
        clearInterval(refreshInterval)
        window.removeEventListener('mousemove', handleUserActivity)
        window.removeEventListener('keydown', handleUserActivity)
        window.removeEventListener('click', handleUserActivity)
        window.removeEventListener('scroll', handleUserActivity)
      }
    }
  }, [isAuthenticatedState, user])

  const login = async (username, password) => {
    try {
      setIsLoading(true)
      const result = await loginUser(username, password)
      
      if (result.success) {
        setUser(result.data)
        setIsAuthenticatedState(true)
        return { success: true }
      } else {
        return { success: false, error: result.error }
      }
    } catch (error) {
      return { success: false, error: 'Login failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await logoutUser()
      setUser(null)
      setIsAuthenticatedState(false)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const value = {
    user,
    isAuthenticated: isAuthenticatedState,
    isLoading,
    login,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
